/**
 * Shared filter bar for Dashboard and Properties. Composable; default = no filter.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FilterX, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FleetFilterState, StatusFilter, ProviderFilter, GridModeFilter } from '@/lib/filters';
import { hasActiveFilters } from '@/lib/filters';

interface FleetFilterBarProps {
  state: FleetFilterState;
  onChange: (state: FleetFilterState) => void;
  onClear: () => void;
  availableTags?: string[];
  className?: string;
  /** When true, use filled button when no filters so the control is easy to spot (e.g. fleet dashboard). */
  prominent?: boolean;
}

export function FleetFilterBar({
  state,
  onChange,
  onClear,
  availableTags = [],
  className,
  prominent = false,
}: FleetFilterBarProps) {
  const active = hasActiveFilters(state);

  const set = (patch: Partial<FleetFilterState>) => {
    onChange({ ...state, ...patch });
  };

  const toggleTag = (tag: string) => {
    const normalized = tag.trim().toLowerCase();
    if (!normalized) return;
    const next = state.tags.includes(normalized)
      ? state.tags.filter((t) => t !== normalized)
      : [...state.tags, normalized];
    set({ tags: next });
  };

  const buttonVariant = prominent && !active ? 'default' : 'outline';

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={buttonVariant}
            size="sm"
            className={cn(active && 'border-primary/50')}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {active && (
              <span className="ml-1 rounded-full bg-primary/20 px-1.5 text-xs">
                on
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Status</Label>
              <Select
                value={state.status ?? 'all'}
                onValueChange={(v) =>
                  set({ status: v === 'all' ? null : (v as StatusFilter) })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="stale">Stale</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Provider</Label>
              <Select
                value={state.provider ?? 'all'}
                onValueChange={(v) =>
                  set({ provider: v === 'all' ? null : (v as ProviderFilter) })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="solark">Sol-Ark</SelectItem>
                  <SelectItem value="eg4">EG4</SelectItem>
                  <SelectItem value="franklinwh">FranklinWH</SelectItem>
                  <SelectItem value="solaredge">SolarEdge</SelectItem>
                  <SelectItem value="enphase">Enphase</SelectItem>
                  <SelectItem value="tesla">Tesla</SelectItem>
                  <SelectItem value="solis">Solis</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Grid mode</Label>
              <Select
                value={
                  state.gridMode === 'grid-tied' || state.gridMode === 'hybrid'
                    ? 'grid-primary'
                    : (state.gridMode ?? 'all')
                }
                onValueChange={(v) =>
                  set({
                    gridMode: v === 'all' ? null : (v as GridModeFilter),
                  })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="off-grid">Off-grid</SelectItem>
                  <SelectItem value="grid-primary">Grid-primary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">SOC min %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="—"
                  className="mt-1"
                  value={state.socMin ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    set({ socMin: v === '' ? null : Math.max(0, Math.min(100, Number(v))) });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">SOC max %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="—"
                  className="mt-1"
                  value={state.socMax ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    set({ socMax: v === '' ? null : Math.max(0, Math.min(100, Number(v))) });
                  }}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Last update (stale &gt; min)</Label>
              <Select
                value={
                  state.lastUpdateStaleMinutes != null
                    ? String(state.lastUpdateStaleMinutes)
                    : 'none'
                }
                onValueChange={(v) =>
                  set({
                    lastUpdateStaleMinutes:
                      v === 'none' ? null : Number(v),
                  })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any</SelectItem>
                  <SelectItem value="15">&gt; 15 min</SelectItem>
                  <SelectItem value="30">&gt; 30 min</SelectItem>
                  <SelectItem value="60">&gt; 1 hr</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {availableTags.length > 0 && (
              <div>
                <Label className="text-xs">Tags</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {availableTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        'rounded-md border px-2 py-1 text-xs transition-colors',
                        state.tags.includes(tag.toLowerCase())
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:bg-muted'
                      )}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {active && (
        <Button variant="ghost" size="sm" onClick={onClear}>
          <FilterX className="w-4 h-4 mr-2" />
          Clear
        </Button>
      )}
    </div>
  );
}
